import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Breadcrumb,
  Button,
  Label,
  Select,
  Spinner,
  Textarea,
} from "flowbite-react";
import {
  HiHome,
  HiSpeakerphone,
  HiPlay,
  HiStop,
  HiDownload,
} from "react-icons/hi";
import { toast } from "react-toastify";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { GOOGLE_API_KEY, TTS_ENDPOINT } from "@/constants/google";

type GoogleVoice = {
  name: string;
  languageCodes: string[];
  ssmlGender: "MALE" | "FEMALE" | "NEUTRAL" | "SSML_VOICE_GENDER_UNSPECIFIED";
  naturalSampleRateHertz: number;
};

const LANGUAGE_LABELS: Record<string, string> = {
  "tr-TR": "Türkçe (Türkiye)",
  "en-US": "English (US)",
  "en-GB": "English (UK)",
  "de-DE": "Deutsch",
  "fr-FR": "Français",
  "es-ES": "Español",
  "it-IT": "Italiano",
  "ar-XA": "العربية",
  "ru-RU": "Русский",
  "ja-JP": "日本語",
};

const TextToSpeechPage: FC = function () {
  const [text, setText] = useState<string>(
    "Merhaba, bu Google Text-to-Speech demosu.",
  );
  const [voices, setVoices] = useState<GoogleVoice[]>([]);
  const [voicesLoading, setVoicesLoading] = useState<boolean>(true);
  const [voicesError, setVoicesError] = useState<string | null>(null);

  const [languageCode, setLanguageCode] = useState<string>("tr-TR");
  const [voiceName, setVoiceName] = useState<string>("");
  const [speakingRate, setSpeakingRate] = useState<number>(1.0);
  const [pitch, setPitch] = useState<number>(0);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBase64, setAudioBase64] = useState<string | null>(null);
  const [synthesizing, setSynthesizing] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchVoices = async () => {
      setVoicesLoading(true);
      setVoicesError(null);
      try {
        const res = await fetch(
          `${TTS_ENDPOINT}/voices?key=${GOOGLE_API_KEY}`,
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(
            errBody?.error?.message || `HTTP ${res.status}`,
          );
        }
        const data = (await res.json()) as { voices: GoogleVoice[] };
        setVoices(data.voices || []);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setVoicesError(msg);
        toast.error(`Sesler yüklenemedi: ${msg}`);
      } finally {
        setVoicesLoading(false);
      }
    };
    fetchVoices();
  }, []);

  const availableLanguages = useMemo(() => {
    const set = new Set<string>();
    voices.forEach((v) => v.languageCodes.forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, [voices]);

  const filteredVoices = useMemo(
    () =>
      voices
        .filter((v) => v.languageCodes.includes(languageCode))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [voices, languageCode],
  );

  useEffect(() => {
    if (filteredVoices.length === 0) {
      setVoiceName("");
      return;
    }
    if (!filteredVoices.some((v) => v.name === voiceName)) {
      setVoiceName(filteredVoices[0].name);
    }
  }, [filteredVoices, voiceName]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const base64ToBlob = (base64: string, mime = "audio/mp3"): Blob => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  const synthesize = async () => {
    if (!text.trim()) {
      toast.warn("Önce bir metin girin.");
      return;
    }
    if (!voiceName) {
      toast.warn("Bir ses seçin.");
      return;
    }

    setSynthesizing(true);
    try {
      const res = await fetch(
        `${TTS_ENDPOINT}/text:synthesize?key=${GOOGLE_API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            input: { text },
            voice: { languageCode, name: voiceName },
            audioConfig: {
              audioEncoding: "MP3",
              speakingRate,
              pitch,
            },
          }),
        },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error?.message || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { audioContent: string };
      const blob = base64ToBlob(data.audioContent);

      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;

      setAudioBase64(data.audioContent);
      setAudioUrl(url);
      toast.success("Ses oluşturuldu.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Sentez başarısız: ${msg}`);
    } finally {
      setSynthesizing(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } else {
      audioRef.current.play();
    }
  };

  const download = () => {
    if (!audioBase64) return;
    const blob = base64ToBlob(audioBase64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeVoice = voiceName.replace(/[^a-zA-Z0-9-_]/g, "_");
    a.href = url;
    a.download = `tts_${safeVoice}_${Date.now()}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const languageOptions = useMemo(() => {
    const preferred = Object.keys(LANGUAGE_LABELS).filter((c) =>
      availableLanguages.includes(c),
    );
    const others = availableLanguages.filter(
      (c) => !LANGUAGE_LABELS[c],
    );
    return [...preferred, ...others];
  }, [availableLanguages]);

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item href="/">
            <div className="flex items-center gap-x-3">
              <HiHome className="text-xl" />
              <span className="dark:text-white">Ana Sayfa</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Text to Speech</Breadcrumb.Item>
        </Breadcrumb>

        <div className="mb-4 flex items-center gap-3">
          <HiSpeakerphone className="text-3xl text-blue-600" />
          <h1 className="text-2xl font-semibold dark:text-white">
            Google Text-to-Speech Demo
          </h1>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4">
            <Label htmlFor="tts-text" value="Metin" />
            <Textarea
              id="tts-text"
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Sese dönüştürülecek metni buraya yazın..."
              maxLength={5000}
            />
            <div className="mt-1 text-right text-xs text-gray-500 dark:text-gray-400">
              {text.length} / 5000
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="tts-language" value="Dil" />
              <Select
                id="tts-language"
                value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}
                disabled={voicesLoading || !!voicesError}
              >
                {languageOptions.map((code) => (
                  <option key={code} value={code}>
                    {LANGUAGE_LABELS[code] || code} ({code})
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="tts-voice" value="Ses" />
              <Select
                id="tts-voice"
                value={voiceName}
                onChange={(e) => setVoiceName(e.target.value)}
                disabled={voicesLoading || filteredVoices.length === 0}
              >
                {filteredVoices.map((v) => (
                  <option key={v.name} value={v.name}>
                    {v.name} — {v.ssmlGender.toLowerCase()}
                  </option>
                ))}
              </Select>
              {voicesLoading && (
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <Spinner size="xs" /> Sesler yükleniyor...
                </div>
              )}
              {voicesError && (
                <div className="mt-1 text-xs text-red-600">
                  {voicesError}
                </div>
              )}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label
                htmlFor="tts-rate"
                value={`Hız: ${speakingRate.toFixed(2)}x`}
              />
              <input
                id="tts-rate"
                type="range"
                min={0.25}
                max={4}
                step={0.05}
                value={speakingRate}
                onChange={(e) => setSpeakingRate(parseFloat(e.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 dark:bg-gray-700"
              />
            </div>
            <div>
              <Label
                htmlFor="tts-pitch"
                value={`Ton: ${pitch.toFixed(1)}`}
              />
              <input
                id="tts-pitch"
                type="range"
                min={-20}
                max={20}
                step={0.5}
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 dark:bg-gray-700"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              color="blue"
              onClick={synthesize}
              disabled={synthesizing || !voiceName || !text.trim()}
            >
              {synthesizing ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Sentezleniyor...
                </>
              ) : (
                <>
                  <HiSpeakerphone className="mr-2 text-lg" />
                  Sese Dönüştür
                </>
              )}
            </Button>

            <Button
              color="light"
              onClick={togglePlay}
              disabled={!audioUrl}
            >
              {isPlaying ? (
                <>
                  <HiStop className="mr-2 text-lg" /> Durdur
                </>
              ) : (
                <>
                  <HiPlay className="mr-2 text-lg" /> Oynat
                </>
              )}
            </Button>

            <Button color="success" onClick={download} disabled={!audioBase64}>
              <HiDownload className="mr-2 text-lg" /> MP3 İndir
            </Button>
          </div>

          {audioUrl && (
            <div className="mt-6">
              <audio
                ref={audioRef}
                src={audioUrl}
                controls
                className="w-full"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
            </div>
          )}
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default TextToSpeechPage;
